import React, { useState, useRef, useEffect } from "react";

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      text: "Hello! I'm EcoBot, your X EcoChain assistant. I can help you with airdrops, staking, transactions, and general questions about our green blockchain. How can I assist you today?", 
      sender: "bot", 
      timestamp: new Date() 
    }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getBotResponse = (userMessage) => {
    const message = userMessage.toLowerCase().trim();
    
    if (message.includes('hi') || message.includes('hello') || message.includes('hey')) {
      return "Hello! Welcome to X EcoChain! ⚪ I'm EcoBot, your guide to the green blockchain revolution. How can I help you today?";
    }
    
    if (message.includes('who are you') || message.includes('what are you')) {
      return "I'm EcoBot, the AI assistant for X EcoChain! I help users with airdrops, staking, transactions, and any questions about our eco-friendly blockchain platform.";
    }
    
    if (message.includes('airdrop') || message.includes('claim')) {
      return "The X1 airdrop is currently live! 🎁 Connect your wallet, complete verification, and claim your tokens. Make sure you're on the official website to avoid scams.";
    }
    
    if (message.includes('staking') || message.includes('stake') || message.includes('earn')) {
      return "Staking X1 tokens helps secure our network while earning you rewards! 📈 Current APY is competitive, and you can unstake at any time. Check the staking section for details.";
    }
    
    if (message.includes('eco') || message.includes('green') || message.includes('environment')) {
      return "X EcoChain uses Proof-of-Authority consensus, making it 99% more energy efficient than traditional blockchains! 🌍 We're committed to sustainable Web3 development.";
    }
    
    if (message.includes('how does') || message.includes('what is') || message.includes('explain')) {
      return "X EcoChain is a layer-2 solution focused on sustainability. We offer fast transactions, low fees, and carbon-neutral operations while maintaining full decentralization.";
    }
    
    if (message.includes('help') || message.includes('support')) {
      return "I can help with: • Airdrop claims • Staking information • Wallet setup • Transaction issues • General questions about X EcoChain. What specific help do you need?";
    }
    
    const defaultResponses = [
      "That's interesting! Could you tell me more about what you're looking for?",
      "I'd love to help with that! Our community on Telegram can provide more specific assistance too.",
      "Great question! For detailed technical information, check our documentation or ask in the community.",
      "I understand! Let me connect you with the right information. What aspect are you most curious about?",
      "Thanks for asking! The X EcoChain team is constantly working on improvements and new features."
    ];
    
    return defaultResponses[Math.floor(Math.random() * defaultResponses.length)];
  };

  const handleSendMessage = () => {
    if (inputMessage.trim() === "") return;

    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: "user",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsTyping(true);

    setTimeout(() => {
      const botResponse = getBotResponse(inputMessage);
      const botMessage = {
        id: messages.length + 2,
        text: botResponse,
        sender: "bot",
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    if (isMinimized) {
      setIsMinimized(false);
    } else {
      setIsOpen(!isOpen);
    }
  };

  const minimizeChat = () => {
    setIsMinimized(true);
  };

  const quickQuestions = [
    "How to claim airdrop?",
    "What is staking?",
    "Is it really eco-friendly?",
    "How to connect wallet?"
  ];

  return (
    <>
      {!isOpen && !isMinimized && (
        <button
          onClick={toggleChat}
          className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-br from-white to-gray-300 rounded-full flex items-center justify-center text-gray-900 font-black shadow-2xl hover:shadow-white/30 hover:scale-110 transition-all duration-300 z-40 group"
        >
          <span className="group-hover:scale-110 transition-transform">💬</span>
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
            Live
          </div>
        </button>
      )}

      {isMinimized && (
        <button
          onClick={toggleChat}
          className="fixed bottom-8 right-8 bg-gradient-to-br from-white to-gray-300 text-gray-900 py-3 px-6 rounded-full flex items-center space-x-3 shadow-2xl hover:shadow-white/30 hover:scale-105 transition-all duration-300 z-40"
        >
          <span>💬</span>
          <span className="font-bold">EcoBot</span>
          <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-end justify-end z-50 p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-white/30 rounded-3xl shadow-2xl w-96 h-[600px] flex flex-col transform transition-all duration-300">
            
            <div className="p-4 border-b border-white/20 bg-gradient-to-r from-gray-800/20 to-gray-700/20 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-white to-gray-300 rounded-2xl flex items-center justify-center text-gray-900 text-lg font-black">
                    🤖
                  </div>
                  <div>
                    <h3 className="font-black text-white">EcoBot Assistant</h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      <span className="text-white text-sm font-medium">Online • 24/7</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={minimizeChat}
                    className="text-gray-400 hover:text-white text-lg transition-colors p-1"
                    title="Minimize"
                  >
                    −
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-red-400 text-xl transition-colors p-1"
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-900/50 to-gray-800/30">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] p-4 rounded-2xl ${
                      message.sender === "user"
                        ? "bg-gradient-to-br from-white to-gray-300 text-gray-900"
                        : "bg-gray-800/60 text-white border border-white/20"
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{message.text}</p>
                    <p className={`text-xs mt-2 ${
                      message.sender === "user" ? "text-gray-700/70" : "text-gray-400"
                    }`}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-800/60 text-white p-4 rounded-2xl border border-white/20">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              
              {messages.length <= 2 && (
                <div className="space-y-2 mt-4">
                  <p className="text-xs text-gray-400 text-center">Try asking:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {quickQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => setInputMessage(question)}
                        className="text-xs bg-gray-800/40 hover:bg-gray-700/60 text-gray-300 px-3 py-2 rounded-full border border-white/20 transition-all duration-200"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            <div className="p-4 border-t border-white/20 bg-gradient-to-r from-gray-900 to-black rounded-b-3xl">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me about X EcoChain..."
                  className="flex-1 px-4 py-3 bg-gray-800/40 border border-gray-600 rounded-xl focus:outline-none focus:border-white focus:ring-2 focus:ring-white focus:ring-opacity-20 text-white placeholder-gray-400 transition-all duration-300"
                  disabled={isTyping}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={isTyping || inputMessage.trim() === ""}
                  className="px-6 bg-gradient-to-br from-white to-gray-300 hover:from-gray-200 hover:to-gray-400 text-gray-900 font-bold rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  Send
                </button>
              </div>
              <p className="text-xs text-gray-400 text-center mt-3">
                EcoBot • Smart Assistant • Powered by X EcoChain
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;